// Modal dialogs on the native <dialog> element (showModal): the browser puts it in the top layer, makes the rest of
// the page inert and gives Escape for free. On top of that: a Tab trap (so focus cannot wander to the browser chrome
// and back into nothing), overlay click to dismiss, focus restore to the opener, page scroll lock, and side sheets
// (placement left/right) for the shell drawer and detail panels. iOS presents these as sheets with Radius.xl corners.

import { X } from 'lucide-react';
import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
  type RefObject,
  type SyntheticEvent,
} from 'react';
import { Button, IconButton } from './Button';
import { cn } from './cn';
import { Input } from './Field';
import { focusIfConnected, getFocusable, trapTab } from './focus';
import { useLatest } from './hooks';

export type DialogSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';
export type DialogPlacement = 'center' | 'left' | 'right';

const WIDTH: Record<DialogSize, string> = {
  sm: 'max-w-[400px]',
  md: 'max-w-[520px]',
  lg: 'max-w-[720px]',
  xl: 'max-w-[960px]',
  full: 'max-w-[min(1400px,calc(100vw-2rem))]',
};

const SHEET_WIDTH: Record<DialogSize, string> = {
  sm: 'w-[min(320px,100vw)]',
  md: 'w-[min(420px,100vw)]',
  lg: 'w-[min(560px,100vw)]',
  xl: 'w-[min(720px,100vw)]',
  full: 'w-screen',
};

export interface DialogProps {
  open: boolean;
  /** Called on Escape, overlay click and the close button (only when `dismissible`). */
  onClose: () => void;
  title: ReactNode;
  /** Visually hide the title (it still names the dialog for screen readers). */
  hideTitle?: boolean;
  description?: ReactNode;
  children?: ReactNode;
  /** Buttons row at the bottom (right-aligned). */
  footer?: ReactNode;
  size?: DialogSize;
  placement?: DialogPlacement;
  /** false while something is saving: Escape, overlay and the close button do nothing. Default true. */
  dismissible?: boolean;
  hideCloseButton?: boolean;
  /** Element to focus on open; else the first [autofocus] / [data-autofocus], else the first field, else the panel. */
  initialFocus?: RefObject<HTMLElement | null>;
  /** Drop the body's padding (drawers and panels that lay out their own edges). */
  flush?: boolean;
  className?: string;
  bodyClassName?: string;
}

/** Renders nothing while closed, so a dialog's form state resets each time it opens. */
export function Dialog(props: DialogProps) {
  if (!props.open) return null;
  return <OpenDialog {...props} />;
}

// Nested dialogs share one scroll lock.
let scrollLocks = 0;
let savedOverflow = '';

function lockScroll(): () => void {
  if (scrollLocks === 0) {
    savedOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
  }
  scrollLocks += 1;
  return () => {
    scrollLocks = Math.max(0, scrollLocks - 1);
    if (scrollLocks === 0) document.documentElement.style.overflow = savedOverflow;
  };
}

function OpenDialog({
  onClose,
  title,
  hideTitle,
  description,
  children,
  footer,
  size = 'md',
  placement = 'center',
  dismissible = true,
  hideCloseButton,
  initialFocus,
  flush = false,
  className,
  bodyClassName,
}: DialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const pointerDownOnOverlay = useRef(false);
  const baseId = useId();
  const titleId = `dlg${baseId}-title`;
  const descriptionId = description != null ? `dlg${baseId}-desc` : undefined;
  const latest = useLatest({ onClose, dismissible });

  useLayoutEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const opener = document.activeElement;
    if (!dialog.open) {
      try {
        dialog.showModal();
      } catch {
        // Not connected yet or an old engine: fall back to a plain open dialog (still styled as a modal).
        dialog.setAttribute('open', '');
      }
    }
    const unlock = lockScroll();
    const target =
      initialFocus?.current ??
      panelRef.current?.querySelector<HTMLElement>('[autofocus],[data-autofocus]') ??
      (bodyRef.current ? getFocusable(bodyRef.current)[0] : undefined) ??
      panelRef.current;
    target?.focus({ preventScroll: true });
    return () => {
      unlock();
      if (dialog.open) dialog.close();
      focusIfConnected(opener);
    };
    // Runs once per opening (the component mounts when `open` turns true); initialFocus is read on open only.
  }, []);

  const requestClose = () => {
    if (latest.current.dismissible) latest.current.onClose();
  };

  // Escape fires `cancel`. Prevent the native close and let React state decide.
  const onCancel = (event: SyntheticEvent<HTMLDialogElement>) => {
    event.preventDefault();
    requestClose();
  };

  // Some engines force-close after repeated Escapes even when `cancel` is prevented. If the element closed while
  // React still renders it open, either report the close or re-open (when not dismissible). A `close` event that
  // arrives while the dialog is open again (StrictMode's mount/unmount/mount) is stale and ignored.
  const onNativeClose = () => {
    const dialog = dialogRef.current;
    if (!dialog || dialog.open) return;
    if (latest.current.dismissible) {
      latest.current.onClose();
    } else {
      try {
        dialog.showModal();
      } catch {
        dialog.setAttribute('open', '');
      }
    }
  };

  // Overlay = the <dialog> box itself (it fills the viewport; the panel sits inside). Require both press and release
  // on it, so a text selection dragged out of the panel does not close the dialog.
  const onMouseDown = (event: MouseEvent<HTMLDialogElement>) => {
    pointerDownOnOverlay.current = event.target === event.currentTarget;
  };
  const onClick = (event: MouseEvent<HTMLDialogElement>) => {
    if (pointerDownOnOverlay.current && event.target === event.currentTarget) requestClose();
    pointerDownOnOverlay.current = false;
  };

  const sheet = placement !== 'center';

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      aria-modal="true"
      onCancel={onCancel}
      onClose={onNativeClose}
      onMouseDown={onMouseDown}
      onClick={onClick}
      onKeyDown={(e) => {
        if (panelRef.current) trapTab(e, panelRef.current);
      }}
      className={cn(
        'fixed inset-0 m-0 h-dvh max-h-none w-screen max-w-none overflow-hidden border-0 bg-transparent text-fg',
        // `flex` only while open: a closed <dialog> must keep the UA's display:none.
        'hidden open:flex',
        placement === 'center' && 'items-center justify-center p-4',
        placement === 'left' && 'justify-start p-0',
        placement === 'right' && 'justify-end p-0',
      )}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        className={cn(
          'relative flex flex-col bg-surface shadow-pop outline-none',
          sheet
            ? cn('h-full', SHEET_WIDTH[size], placement === 'left' ? 'animate-slide-in-left' : 'animate-slide-in-right')
            : cn('max-h-[calc(100dvh-2rem)] w-full rounded-sheet border border-line animate-pop-in', WIDTH[size]),
          className,
        )}
      >
        <div className={cn('flex shrink-0 items-start gap-3 px-6 pt-5', hideTitle ? 'pb-0' : 'pb-3')}>
          <div className={cn('min-w-0 flex-1', hideTitle && 'sr-only')}>
            <h2 id={titleId} className="text-lg leading-snug font-bold text-fg">
              {title}
            </h2>
            {description != null && (
              <p id={descriptionId} className="mt-1 text-sm text-fg-muted">
                {description}
              </p>
            )}
          </div>
          {dismissible && !hideCloseButton && (
            <IconButton
              label="Close"
              icon={<X />}
              size="sm"
              round
              onClick={requestClose}
              className={cn('-mt-0.5 -mr-2', hideTitle && 'ml-auto')}
            />
          )}
        </div>
        <div
          ref={bodyRef}
          className={cn('min-h-0 flex-1 overflow-y-auto gt-scroll', !flush && 'px-6 pb-5', bodyClassName)}
        >
          {children}
        </div>
        {footer != null && (
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-line px-6 py-4">
            {footer}
          </div>
        )}
      </div>
    </dialog>
  );
}

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * The confirmed action. May return a promise: the button spins, Escape and Cancel are disabled meanwhile, and a
   * rejection is shown in the dialog (which stays open). On success the dialog calls `onClose`.
   */
  onConfirm: () => void | Promise<unknown>;
  title: ReactNode;
  message?: ReactNode;
  /** Extra content between the message and the buttons (a reason picker, a list of what will change). */
  children?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Red confirm button (deletes, removals). */
  destructive?: boolean;
  /** "Type X to confirm": the confirm button stays disabled until the field equals this text (trimmed). */
  confirmText?: string;
  /** Disable the confirm button for reasons of the caller's own (an unpicked reason). */
  confirmDisabled?: boolean;
  /** External busy flag (when the caller tracks the mutation itself). */
  busy?: boolean;
}

export function ConfirmDialog({ open, ...rest }: ConfirmDialogProps) {
  if (!open) return null;
  return <OpenConfirm open {...rest} />;
}

function errorText(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  if (typeof error === 'string' && error) return error;
  return 'Something went wrong. Please try again.';
}

function OpenConfirm({
  onClose,
  onConfirm,
  title,
  message,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  confirmText,
  confirmDisabled = false,
  busy = false,
}: ConfirmDialogProps) {
  const [typed, setTyped] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const typedRef = useRef<HTMLInputElement>(null);
  const textId = useId();

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const working = pending || busy;
  const textOk = confirmText === undefined || typed.trim() === confirmText.trim();

  const confirm = async () => {
    if (working || !textOk || confirmDisabled) return;
    setError(null);
    try {
      const result = onConfirm();
      if (result && typeof (result as Promise<unknown>).then === 'function') {
        setPending(true);
        await result;
      }
      if (mounted.current) onClose();
    } catch (e) {
      if (mounted.current) setError(errorText(e));
    } finally {
      if (mounted.current) setPending(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={title}
      size="sm"
      dismissible={!working}
      // Destructive confirms start on Cancel so Enter never deletes by accident; type-to-confirm starts in the field.
      initialFocus={confirmText !== undefined ? typedRef : destructive ? cancelRef : undefined}
      footer={
        <>
          <Button ref={cancelRef} variant="secondary" onClick={onClose} disabled={working}>
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? 'destructive' : 'primary'}
            onClick={() => void confirm()}
            loading={working}
            disabled={!textOk || confirmDisabled}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void confirm();
        }}
        className="flex flex-col gap-3"
      >
        {message != null && <div className="text-sm text-fg-muted">{message}</div>}
        {children}
        {confirmText !== undefined && (
          <div className="flex flex-col gap-1.5">
            <label htmlFor={`ct${textId}`} className="text-sm text-fg">
              Type <span className="rounded bg-surface-3 px-1 font-mono font-semibold select-all">{confirmText}</span>{' '}
              to confirm.
            </label>
            <Input
              ref={typedRef}
              id={`ct${textId}`}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              disabled={working}
            />
          </div>
        )}
        {error && (
          <p role="alert" className="rounded-control bg-danger-soft px-3 py-2 text-sm font-medium text-danger-text">
            {error}
          </p>
        )}
      </form>
    </Dialog>
  );
}
